import { useMemo, useState } from 'react';
import { Badge, Box, Button, Group, Modal, Stack, Switch, Text, Tooltip } from '@mantine/core';
import type { NutritionVariant, Zygosity } from '@mdev/common';
import { CHROMOSOME_LENGTHS, calculateRelativePosition } from '../../data/chromosome-lengths';
import { VariantDetailDialog } from './VariantDetailDialog';

interface GenomeMapProps {
  isOpen: boolean;
  onClose: () => void;
  allVariants: NutritionVariant[];
  selectedRsId?: string;
}

interface ChromosomeMarker {
  variant: NutritionVariant;
  relativePosition: number;
  chromosome: string;
  position: number;
}

const CHROMOSOME_ORDER = [
  '1', '2', '3', '4', '5', '6', '7', '8', '9', '10',
  '11', '12', '13', '14', '15', '16', '17', '18', '19', '20',
  '21', '22', 'X', 'Y',
];

const ZYG_COLORS: Record<Zygosity, { bg: string; text: string }> = {
  '+/+': { bg: '#ff6b6b', text: '#fff' },
  '+/-': { bg: '#ffa726', text: '#fff' },
  '-/-': { bg: '#42a5f5', text: '#fff' },
  missing: { bg: '#9e9e9e', text: '#fff' },
};

const getAlleles = (variant: NutritionVariant): [string, string] => {
  const cleaned = variant.genotype.replace(/[^ATCG]/g, '');
  if (cleaned.length === 2) return [cleaned[0], cleaned[1]];
  return ['?', '?'];
};

const isHighRisk = (z: Zygosity) => z === '+/+' || z === '+/-';

export function GenomeMap({ isOpen, onClose, allVariants, selectedRsId }: GenomeMapProps) {
  const [selectedVariant, setSelectedVariant] = useState<NutritionVariant | null>(null);
  const [variantDialogOpen, setVariantDialogOpen] = useState(false);
  const [compactMode, setCompactMode] = useState(false);
  const [showOnlyHighRisk, setShowOnlyHighRisk] = useState(false);

  const chromosomeData = useMemo(() => {
    const data: Record<string, ChromosomeMarker[]> = {};
    CHROMOSOME_ORDER.forEach((chr) => (data[chr] = []));

    allVariants.forEach((variant) => {
      if (showOnlyHighRisk && !isHighRisk(variant.zygosity)) return;
      const chromosome = variant.chromosome?.replace('chr', '');
      const position = variant.position_bp || variant.position;
      if (chromosome && position && CHROMOSOME_LENGTHS[chromosome]) {
        data[chromosome].push({
          variant,
          relativePosition: calculateRelativePosition(chromosome, position),
          chromosome,
          position,
        });
      }
    });

    return data;
  }, [allVariants, showOnlyHighRisk]);

  const handleVariantClick = (variant: NutritionVariant) => {
    setSelectedVariant(variant);
    setVariantDialogOpen(true);
  };

  const chromosomeHeight = compactMode ? 20 : 30;
  const barHeight = compactMode ? 8 : 12;

  return (
    <>
      <Modal
        opened={isOpen}
        onClose={onClose}
        title="Genome Map Visualization"
        size="xl"
        styles={{ content: { maxHeight: '90vh', overflow: 'auto' } }}
      >
        <Stack gap="md">
          <Group justify="space-between">
            <Group gap="md">
              <Switch
                label="Compact view"
                checked={compactMode}
                onChange={(e) => setCompactMode(e.currentTarget.checked)}
              />
              <Switch
                label="Show only risk variants"
                checked={showOnlyHighRisk}
                onChange={(e) => setShowOnlyHighRisk(e.currentTarget.checked)}
              />
            </Group>

            <Badge color="blue" variant="light">
              {Object.values(chromosomeData).flat().length} variants mapped
            </Badge>
          </Group>

          {selectedRsId && (
            <Text size="sm" c="blue">
              Highlighting: {selectedRsId}
            </Text>
          )}

          <Stack gap={compactMode ? 'xs' : 'sm'}>
            {CHROMOSOME_ORDER.map((chromosome) => {
              const markers = chromosomeData[chromosome] || [];

              return (
                <Box key={chromosome}>
                  <Group gap="sm" align="center" mb={4}>
                    <Text size={compactMode ? 'xs' : 'sm'} fw={500} miw={30}>
                      Chr {chromosome}
                    </Text>
                    <Text size="xs" c="dimmed">
                      {markers.length} variant{markers.length !== 1 ? 's' : ''}
                    </Text>
                  </Group>

                  <Box
                    style={{
                      position: 'relative',
                      height: chromosomeHeight,
                      marginBottom: compactMode ? 8 : 12,
                    }}
                  >
                    <Box
                      style={{
                        position: 'absolute',
                        top: (chromosomeHeight - barHeight) / 2,
                        left: 0,
                        width: '100%',
                        height: barHeight,
                        backgroundColor: '#e9ecef',
                        borderRadius: 2,
                      }}
                    />

                    {markers.map((marker, idx) => {
                      const [allele1, allele2] = getAlleles(marker.variant);
                      const colors = ZYG_COLORS[marker.variant.zygosity];
                      const isSelected = marker.variant.rsid === selectedRsId;

                      return (
                        <Tooltip
                          key={`${marker.variant.rsid}-${idx}`}
                          label={
                            <Box>
                              <Text size="xs" fw={500}>
                                {marker.variant.rsid}
                              </Text>
                              <Text size="xs">
                                {marker.variant.gene} - {marker.variant.variantName}
                              </Text>
                              <Text size="xs">
                                Chr {chromosome}:{marker.position.toLocaleString()}
                              </Text>
                              <Text size="xs">Genotype: {marker.variant.genotype}</Text>
                              <Text size="xs">Zygosity: {marker.variant.zygosity}</Text>
                            </Box>
                          }
                        >
                          <Box
                            style={{
                              position: 'absolute',
                              left: `${marker.relativePosition * 100}%`,
                              top: 0,
                              height: chromosomeHeight,
                              width: compactMode ? 16 : 24,
                              transform: 'translateX(-50%)',
                              cursor: 'pointer',
                              zIndex: isSelected ? 10 : 1,
                            }}
                            onClick={() => handleVariantClick(marker.variant)}
                          >
                            <Box
                              style={{
                                width: '100%',
                                height: '100%',
                                borderRadius: 3,
                                backgroundColor: colors.bg,
                                border: isSelected
                                  ? '2px solid #339af0'
                                  : '1px solid rgba(0,0,0,0.1)',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: compactMode ? 8 : 10,
                                fontWeight: 600,
                                color: colors.text,
                                boxShadow: isSelected
                                  ? '0 2px 8px rgba(51, 154, 240, 0.3)'
                                  : 'none',
                              }}
                            >
                              <div>{allele1}</div>
                              <div>{allele2}</div>
                            </Box>
                          </Box>
                        </Tooltip>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Stack>

          <Group justify="flex-end" mt="lg">
            <Button variant="light" onClick={onClose}>
              Close
            </Button>
          </Group>
        </Stack>
      </Modal>

      <VariantDetailDialog
        variant={selectedVariant}
        zygDesc={selectedVariant?.zygDesc}
        isOpen={variantDialogOpen}
        onClose={() => setVariantDialogOpen(false)}
      />
    </>
  );
}
